// src/lib/calculationService.ts
//
// BekalNanti — Milestone P5: Calculation Service
// Prepares calculation inputs from canonical P2/P3/P4 state and runs the deterministic engine.
// ZERO AI, ZERO external dependencies, strictly reproducible.

import { GoalRecord, JourneyData } from '../journey/types'
import { CalculationInput, CalculationResult, CalculationAssumptions } from './calculation/types'
import { calculateEducationPlan, DEFAULT_ANNUAL_INFLATION_RATE } from './calculation/educationCalculation'

/**
 * Build CalculationInput for a specific goal from loaded JourneyData.
 */
export function buildCalculationInput(
  goal: GoalRecord,
  journey: JourneyData,
  assumptions?: Partial<CalculationAssumptions>
): CalculationInput {
  const financialSummary = journey.financialReality?.summary

  return {
    target: {
      goalId: goal.id,
      goalName: goal.name,
      goalType: goal.goalType,
      targetYear: goal.target?.targetYear ?? null,
      targetDate: goal.target?.targetDate ?? null,
      durationMonths: goal.target?.durationMonths ?? null,
      targetCurrency: goal.target?.targetCurrency || 'IDR',
      targetStage: goal.target?.targetStage ?? null,
      programName: goal.target?.programName ?? null,
      targetCountry: goal.target?.targetCountry ?? null,
    },
    costItems: goal.costItems || [],
    fundingSources: goal.fundingSources || [],
    financialReality: financialSummary
      ? {
          totalMonthlyIncome: financialSummary.totalMonthlyIncome,
          totalMonthlyExpenses: financialSummary.totalMonthlyExpenses,
          totalMonthlyCommitments: financialSummary.totalMonthlyCommitments,
          netMonthlyCapacity: financialSummary.netMonthlyCapacity,
          liquidSavings: financialSummary.availableSavings ?? financialSummary.totalSavings ?? 0,
        }
      : null,
    assumptions: {
      annualInflationRate: assumptions?.annualInflationRate ?? DEFAULT_ANNUAL_INFLATION_RATE,
      planningAnchorYear: assumptions?.planningAnchorYear ?? new Date().getFullYear(),
    },
    planningCurrency: journey.profile?.defaultCurrency || 'IDR',
  }
}

/**
 * Run deterministic calculation for a goal given the user's active JourneyData.
 */
export function runGoalCalculation(
  goal: GoalRecord,
  journey: JourneyData,
  assumptions?: Partial<CalculationAssumptions>
): CalculationResult {
  const input = buildCalculationInput(goal, journey, assumptions)
  return calculateEducationPlan(input)
}
